import * as React from "react";
import { tokens } from "@fluentui/react-components";
import { Dismiss12Regular, PersonAdd20Regular, TaskListSquareLtr20Regular } from "@fluentui/react-icons";
import { openMailContactAssign } from "./MailContactAssignWindow";
import { openMailVorgangPicker } from "./MailVorgangPickerWindow";
import type { OpenWindowConfig } from "../windowManager/WindowManagerContext";
import { vorgangService } from "../../services/vorgangService";
import {
    zieleBeschriften,
    type AntwortZuordnungAngebot,
    type AntwortZuordnungWahl,
} from "./antwortZuordnung";
import type { MailVorgangZiel } from "./mailVorgangAssign";

/**
 * Zuweisen beim Antworten (User 02.11.2026) — die Leiste im Antwort-Fenster.
 *
 * Steht nur, wenn die Ursprungsmail an keinem Vorgang hängt. Gewählt wird mit
 * den Fenstern, die der Posteingang schon hat („Kontakt zuweisen" und
 * „Bestehendem Vorgang zuordnen"); wirksam wird die Wahl erst beim Senden
 * (siehe `antwortZuordnung.ts`). Optik wie die übrigen Zeilen des Fensters,
 * Aubergine als Akzent (kvp-rd).
 */

const AUB = "#4d2051";

const S: Record<string, React.CSSProperties> = {
    zeile: {
        borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
        padding: "6px 12px",
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 8,
        background: "#faf7fa",
        fontSize: 12.5,
    },
    label: { fontWeight: 600, color: AUB, marginRight: 2 },
    text: { color: tokens.colorNeutralForeground3 },
    knopf: {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        border: `1px solid ${AUB}`,
        background: "#fff",
        color: AUB,
        borderRadius: 6,
        padding: "2px 8px",
        fontSize: 12,
        fontWeight: 600,
        cursor: "pointer",
        fontFamily: "inherit",
    },
    chip: {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        background: "#efe6ef",
        color: AUB,
        borderRadius: 999,
        padding: "2px 4px 2px 10px",
        fontSize: 12,
        fontWeight: 600,
        maxWidth: "100%",
    },
    chipX: {
        display: "inline-flex",
        border: "none",
        background: "transparent",
        color: AUB,
        cursor: "pointer",
        padding: 2,
        borderRadius: 999,
    },
};

type Props = {
    angebot: AntwortZuordnungAngebot;
    wahl: AntwortZuordnungWahl;
    onWahl: (w: AntwortZuordnungWahl) => void;
    openWindow: (config: OpenWindowConfig) => void;
    /** Während des Versands ist nichts mehr zu ändern. */
    gesperrt?: boolean;
};

/** Lädt die Titel der gewählten Ziele — schlägt das fehl, bleibt die Nummer. */
async function beschrifte(ziele: MailVorgangZiel[]): Promise<string> {
    const vorgaenge = new Map<number, string>();
    const todos = new Map<number, string>();
    await Promise.all(
        ziele.map(async (z) => {
            try {
                if (z.todoId != null) todos.set(z.todoId, (await vorgangService.getTodo(z.todoId)).titel);
                else if (z.vorgangId != null) {
                    vorgaenge.set(z.vorgangId, (await vorgangService.getVorgang(z.vorgangId)).titel);
                }
            } catch {
                /* Nummer statt Titel */
            }
        }),
    );
    return zieleBeschriften(ziele, { vorgaenge, todos });
}

export const AntwortZuordnungLeiste: React.FC<Props> = ({ angebot, wahl, onWahl, openWindow, gesperrt }) => {
    // Die Fenster melden sich asynchron zurück — immer auf den neuesten Stand aufsetzen.
    const wahlRef = React.useRef(wahl);
    wahlRef.current = wahl;
    const beschriftungRef = React.useRef(0);
    const setze = (teil: Partial<AntwortZuordnungWahl>) => {
        // Sofort mitführen: zwei Rückmeldungen hintereinander sähen sonst den alten Stand.
        wahlRef.current = { ...wahlRef.current, ...teil };
        if (teil.ziele) beschriftungRef.current++;
        onWahl(wahlRef.current);
    };

    const waehleVorgang = (kontaktId: number | null) =>
        openMailVorgangPicker(openWindow, {
            contactId: kontaktId ?? angebot.kontaktZugewiesen?.id ?? angebot.erkannterKontakt?.id ?? null,
            mailBetreff: angebot.ursprung.subject ?? null,
            onAssign: async (ziele) => {
                const lauf = ++beschriftungRef.current;
                wahlRef.current = { ...wahlRef.current, ziele, zieleText: null };
                onWahl(wahlRef.current);
                const text = await beschrifte(ziele);
                // Nur übernehmen, wenn inzwischen nicht anders gewählt wurde.
                if (lauf === beschriftungRef.current) setze({ zieleText: text });
            },
        });

    const waehleKontakt = () =>
        openMailContactAssign(openWindow, {
            senderEmail: angebot.senderEmail,
            senderName: angebot.senderName,
            mail: angebot.ursprung,
            aktuelleZuweisung: wahlRef.current.kontakt,
            erkennung: null,
            fussHinweis:
                "„Nur …“ merkt den Kontakt für Antwort und Ursprungsmail vor; „Weiter“ wählt zusätzlich einen Vorgang oder ein To-do. Zugeordnet wird beim Senden.",
            onResolved: (e) => {
                setze({ kontakt: e.kontakt });
                if (e.weiter === "vorgang") waehleVorgang(e.kontakt?.id ?? null);
            },
        });

    const kontaktOffen = !angebot.kontaktZugewiesen;
    const zielText = wahl.zieleText ?? (wahl.ziele.length ? "Vorgang wird geladen …" : null);

    return (
        <div style={S.zeile} data-testid="antwort-zuordnung">
            <span style={S.label}>Zuordnung</span>
            {!wahl.kontakt && wahl.ziele.length === 0 && (
                <span style={S.text}>
                    {kontaktOffen
                        ? "Die Mail ist noch nicht zugewiesen."
                        : "Die Mail hängt an keinem Vorgang."}
                </span>
            )}
            {wahl.kontakt && (
                <span style={S.chip} title="Antwort und Ursprungsmail werden diesem Kontakt zugewiesen">
                    Kontakt: {wahl.kontakt.displayName}
                    <button
                        type="button"
                        style={S.chipX}
                        aria-label="Kontakt-Zuweisung entfernen"
                        disabled={gesperrt}
                        onClick={() => setze({ kontakt: null })}
                    >
                        <Dismiss12Regular />
                    </button>
                </span>
            )}
            {zielText && (
                <span style={S.chip} title="Antwort und Ursprungsmail landen hier">
                    {zielText}
                    <button
                        type="button"
                        style={S.chipX}
                        aria-label="Vorgangs-Zuordnung entfernen"
                        disabled={gesperrt}
                        onClick={() => setze({ ziele: [], zieleText: null })}
                    >
                        <Dismiss12Regular />
                    </button>
                </span>
            )}
            <span style={{ flex: 1 }} />
            {kontaktOffen && !wahl.kontakt && (
                <button type="button" style={S.knopf} disabled={gesperrt} onClick={waehleKontakt}>
                    <PersonAdd20Regular style={{ width: 14, height: 14 }} /> Kontakt zuweisen…
                </button>
            )}
            <button
                type="button"
                style={S.knopf}
                disabled={gesperrt}
                onClick={() => waehleVorgang(wahl.kontakt?.id ?? null)}
            >
                <TaskListSquareLtr20Regular style={{ width: 14, height: 14 }} />
                {wahl.ziele.length ? "Anderer Vorgang…" : "Vorgang / To-do zuordnen…"}
            </button>
        </div>
    );
};
